import { useState } from 'react';
import { useAccountsCrumb } from './lib/usePhoneCrumb';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useNarrowViewport } from '@/components/kit/useNarrow';
import { Button, Card, Chip, DChip, DataTable, Pane, PaneHead, TabBar } from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { PeriodRow } from './components/PeriodRow';
import {
  amount,
  money,
  monthName,
  periodLabel,
  thisPeriod,
  useExpenseActions,
  useExpenseLines,
  useExpenseMonth,
  useRecurring,
  useRecurringHistory,
  usePayrollRun,
  openExpenseDocument,
  type CategoryRow,
  type ExpenseCategory,
  type ExpenseLine,
  type RecurringLine,
} from './lib/useAccounts';
import { AccountantMenu } from './AccountantMenu';
import { AddRecurringModal, AttachModal, RecordModal } from './components/ExpenseModals';
import { PhoneTabs } from './components/PhoneTabs';

/**
 * Expenses — `g_exp` (4), `g_expzero` (4b), `g_recur` (5) and the three
 * modals. WHAT THE CLINIC SPENDS.
 *
 * POSTED IS NOT PROVEN. An expense exists without its paper — rent posts
 * itself on the first whether or not the landlord has sent anything — so
 * "Undocumented" is a FIGURE on this screen rather than a validation error
 * that would stop the books being kept. Attaching the invoice later is the
 * whole job of board 5c.
 *
 * A RECURRING LINE IS EITHER FIXED OR A REMINDER, NEVER AN ESTIMATE. Rent and
 * a subscription are the same number every month and should not be typed
 * twelve times, so they post themselves. Electricity is different every
 * month — posting a guess as fact is how a profit-and-loss quietly becomes
 * fiction, so a variable line only reminds and somebody records the real
 * number when the bill arrives.
 */

type Tab = 'month' | 'recurring';

export function ExpensesPage() {
  const [params, setParams] = useSearchParams();
  /* A link from Payables names the month, the category and the line it means. */
  const linkedPeriod = params.get('period');
  const [period, setPeriod] = useState(() => (linkedPeriod && /^\d{4}-\d{2}$/.test(linkedPeriod) ? linkedPeriod : thisPeriod()));
  const [category, setCategory] = useState<ExpenseCategory | null>(() => (params.get('category') as ExpenseCategory | null) || null);
  const linkedLine = params.get('line');
  const [recording, setRecording] = useState(false);
  const narrow = useNarrowViewport();
  const [addingRecurring, setAdding] = useState(false);
  const [attaching, setAttaching] = useState<Pick<ExpenseLine, 'id' | 'description' | 'amountMinor' | 'spentOn'> | null>(null);
  const [recurringId, setRecurringId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const roleCheck = useAuthStore((s) => s.roleCheck);
  const canWrite = roleCheck.hasPermission(Permission.EXPENSE_WRITE);

  const tab: Tab = params.get('tab') === 'recurring' ? 'recurring' : 'month';
  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    if (next === 'recurring') p.set('tab', 'recurring');
    else p.delete('tab');
    setParams(p, { replace: true });
    setCategory(null);
    setRecurringId(null);
  };

  usePageMeta({ title: 'Expenses' });

  const { data: month, isLoading, isError } = useExpenseMonth(period);
  const { data: lines } = useExpenseLines(period, category);
  const { data: recurring } = useRecurring(period);
  const act = useExpenseActions();

  const pickedRecurring = (recurring ?? []).find((r) => r.id === recurringId) ?? null;

  const delta =
    month && month.previousMinor > 0
      ? Math.round(((month.totalMinor - month.previousMinor) / month.previousMinor) * 100)
      : null;

  const crumb = useAccountsCrumb(tab === 'recurring' ? 'Expenses' : 'Accounts', tab === 'recurring' ? 'Recurring' : `Expenses · ${periodLabel(period).split(' ')[0]}`, tab === 'recurring' ? '/accountant/expenses' : '/accountant');
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: tab === 'recurring' ? !!pickedRecurring : !!category,
      onPaneClose: () => {
        setCategory(null);
        setRecurringId(null);
      },
      phoneMenu: (close) => <AccountantMenu door="expenses" onClose={close} />,
      title: tab === 'recurring' ? 'Expenses ▸ Recurring' : 'Expenses',
      sub:
        tab === 'recurring'
          ? 'the ones that repeat forever'
          : `${periodLabel(period)} · all branches`,
      search: tab === 'recurring' ? 'Search a recurring line…' : 'Search a supplier, an invoice…',
      searchValue: query,
      onSearchChange: setQuery,
      ...(canWrite
        ? {
            primary:
              tab === 'recurring'
                ? narrow
                  ? undefined
                  : { label: '+ Add recurring', onClick: () => setAdding(true) }
                : { label: '+ Record an expense', onClick: () => setRecording(true) },
          }
        : {}),
      pane: tab === 'recurring' && pickedRecurring ? (
        <RecurringPane
          line={pickedRecurring}
          period={period}
          canWrite={canWrite}
          onAttach={() =>
            pickedRecurring.postedExpenseId
              ? setAttaching({
                  id: pickedRecurring.postedExpenseId,
                  description: pickedRecurring.label,
                  amountMinor: pickedRecurring.amountMinor ?? 0,
                  spentOn: `${period}-${String(pickedRecurring.dayOfMonth).padStart(2, '0')}`,
                })
              : undefined
          }
          onClose={() => setRecurringId(null)}
        />
      ) : category && month ? (
        <CategoryPane
          row={month.categories.find((c) => c.category === category)}
          lines={lines ?? []}
          canWrite={canWrite}
          period={period}
          onAttach={setAttaching}
          highlightId={linkedLine}
          onClose={() => setCategory(null)}
        />
      ) : (
        <Pane
          head={
            <PaneHead
              overline={tab === 'recurring' ? 'Recurring' : 'Selected category'}
              title="Nothing picked"
              sub={
                tab === 'recurring'
                  ? 'every month, until it is stopped'
                  : 'pick a category to see what is in it'
              }
            />
          }
        >
          <div className="emptystate">
            <p className="t-sec">
              {tab === 'recurring'
                ? 'Rent and subscriptions are the same number every month and should never be typed twelve times. Variable bills are a reminder, never an estimate posted as fact.'
                : 'Every line here is money that left the clinic. What it was for, and whether there is paper behind it.'}
            </p>
          </div>
        </Pane>
      ),
    },
    [tab, category, month, lines, canWrite, period, recurringId, recurring, query],
  );

  const categoryRows: TableRow[] = (month?.categories ?? [])
    .filter((c) => !query.trim() || `${c.label} ${c.sub}`.toLowerCase().includes(query.trim().toLowerCase()))
    .map((c) => ({
    id: c.category,
    cells: {
      category: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{c.label}</p>
          <p className="t-cap mut">{c.sub}</p>
        </div>
      ),
      share: (
        <span>
          <span className="acc-bar" aria-hidden="true">
            <span className="acc-bar-fill" style={{ width: `${c.share}%` }} />
          </span>
          <span className="t-cap mut"> {c.share}%</span>
        </span>
      ),
      total: (
        <span>
          <span className="t-strong num">{amount(c.totalMinor)}</span>
          {c.undocumentedMinor ? (
            <span className="t-cap amb"> · {amount(c.undocumentedMinor)} undocumented</span>
          ) : null}
        </span>
      ),
    },
  }));

  const recurringRows: TableRow[] = (recurring ?? [])
    .filter((r) => !query.trim() || `${r.label} ${r.supplierName ?? ''}`.toLowerCase().includes(query.trim().toLowerCase()))
    .map((r) => ({
    id: r.id,
    cells: {
      line: (
        <div style={{ minWidth: 0 }}>
          <p className="t-strong">{r.label}</p>
          <p className="t-cap mut">
            {[r.supplierName, r.paidWith].filter(Boolean).join(' · ') ||
              (r.amountMinor === null ? 'different every month' : 'the same number every month')}
          </p>
        </div>
      ),
      amount:
        r.amountMinor === null ? (
          <span className="mut">variable</span>
        ) : (
          <span className="t-strong num">{amount(r.amountMinor)}</span>
        ),
      day: <span className="mut">the {ordinal(r.dayOfMonth)}</span>,
      state: <RecurringState line={r} />,
    },
  }));

  return (
    <div className="maie desk acc-root">
      {tab === 'recurring' ? null : (
        <PeriodRow period={period} onChange={setPeriod} branches={month?.branches} />
      )}

      <div className="dcontent scroll">
        {narrow && tab === 'recurring' ? null : <PhoneTabs active="expenses" />}
        {narrow && tab === 'recurring' ? null : <TabBar
          label="Expenses"
          items={[
            { key: 'month' as const, label: 'This month' },
            {
              key: 'recurring' as const,
              label: recurring?.length ? `Recurring · ${recurring.length}` : 'Recurring',
            },
          ]}
          active={tab}
          onSelect={setTab}
        />}

        {tab === 'month' ? (
          <>
            {!isLoading && !isError && month && month.totalMinor === 0 ? null : (
            <div className="acc-stats acc-stats-3">
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">{monthName(period)}</p>
                <p className="t-dxl mt8">{isLoading ? '…' : money(month?.totalMinor ?? 0)}</p>
              </div>
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">vs the month before</p>
                <p className={`t-dxl mt8${delta !== null && delta > 0 ? ' amb' : ''}`}>
                  {delta === null ? '—' : `${delta > 0 ? '+' : ''}${delta}%`}
                </p>
                <p className="t-cap mut mt4">
                  {month?.previousMinor ? money(month.previousMinor) : 'nothing to compare to'}
                </p>
              </div>
              <div className="card" style={{ margin: 0 }}>
                <p className="t-ovl mut">Undocumented</p>
                <p className={`t-dxl mt8${month?.undocumentedMinor ? ' amb' : ''}`}>
                  {money(month?.undocumentedMinor ?? 0)}
                </p>
                <p className="t-cap mut mt4">
                  {month?.undocumentedCount
                    ? `${month.undocumentedCount} line${month.undocumentedCount === 1 ? '' : 's'} with no paper behind them`
                    : 'every line has its invoice'}
                </p>
              </div>
            </div>
            )}

            <DataTable
              caption={`Spending in ${periodLabel(period)}`}
              columns={[
                { key: 'category', label: 'Category', width: 58 },
                { key: 'share', label: 'Share of spend', width: 18 },
                { key: 'total', label: 'Amount', width: 24 },
              ]}
              rows={categoryRows}
              selectedId={category}
              onSelect={(r) => setCategory(r.id === category ? null : (r.id as ExpenseCategory))}
              empty={
                <div className="emptystate">
                  {isLoading ? (
                    <p className="t-sec mut">Reading the month…</p>
                  ) : isError ? (
                    <p className="t-sec mut">
                      The month did not come back. Nothing has been changed.
                    </p>
                  ) : (
                    <>
                      <p className="es-t">Nothing typed yet in {periodLabel(period)}</p>
                      <p className="t-sec mut">
                        {month?.postedByRecurring
                          ? `${month.postedByRecurring} recurring line${month.postedByRecurring === 1 ? ' has' : 's have'} posted themselves this month. They live under Recurring until their invoices are attached. Everything else waits for its paper.`
                          : 'Nothing has posted itself and nothing has been recorded. Everything waits for its paper.'}
                      </p>
                      <p className="mt12" style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                        {canWrite ? (
                          <DChip onClick={() => setRecording(true)}>+ Record an expense</DChip>
                        ) : null}
                        <DChip onClick={() => setTab('recurring')}>See the recurring lines</DChip>
                      </p>
                    </>
                  )}
                </div>
              }
            />

            {month && month.totalMinor === 0 ? null : (
            <Card kind="accent" className="mt16">
              <p className="t-sec">
                Message-wallet top-ups and stock consumption post themselves &mdash; the only expenses typed by
                hand are the ones MAIE cannot know.
              </p>
            </Card>
            )}
          </>
        ) : (
          <>
            {narrow ? (
              <>
                {recurringRows.map((row, i) => {
                  const r = (recurring ?? []).find((x) => x.id === row.id);
                  if (!r) return null;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      className="card"
                      style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}
                      onClick={() => setRecurringId(r.id)}
                      data-row={i}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span className="t-strong" style={{ display: 'block' }}>{r.label}</span>
                        <span className="t-cap mut" style={{ display: 'block' }}>
                          {r.amountMinor === null ? `varies · reminder only` : `the ${ordinal(r.dayOfMonth)}`}
                        </span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                        <span className="t-strong num">
                          {r.amountMinor === null ? '—' : money(r.amountMinor)}
                        </span>
                        <Chip kind={r.amountMinor === null ? 'warn' : 'success'} small>
                          {r.amountMinor === null ? 'remind' : 'auto'}
                        </Chip>
                      </span>
                    </button>
                  );
                })}
                {(recurring ?? []).length ? (
                  <>
                    <Card kind="warn">
                      <p className="t-sec">
                        Auto-posted lines still wait for the invoice photo before they count as evidence
                      </p>
                    </Card>
                    <Card>
                      <p className="t-sec">Variable bills are a REMINDER, never an estimate posted as fact</p>
                    </Card>
                  </>
                ) : (
                  <div className="emptystate">
                    <p className="es-t">Nothing repeats yet</p>
                    <p className="t-sec mut">
                      Rent and subscriptions are the same number every month and should never be typed twelve times.
                    </p>
                  </div>
                )}
                {canWrite ? (
                  <button type="button" className="btn btn-primary mt12" onClick={() => setAdding(true)}>
                    + Add a recurring cost
                  </button>
                ) : null}
              </>
            ) : (
              <>
            <p className="t-sec mut mb12">
              Every month, until it is stopped — the {monthName(period)} lines are the ones
              posted · all branches
            </p>
            <DataTable
              caption="Costs that repeat"
              columns={[
                { key: 'line', label: 'Line', width: 46 },
                { key: 'amount', label: 'Amount', width: 14 },
                { key: 'day', label: 'Posts on', width: 14 },
                { key: 'state', label: 'This month', width: 26 },
              ]}
              rows={recurringRows}
              selectedId={recurringId}
              onSelect={(r) => setRecurringId(r.id === recurringId ? null : r.id)}
              empty={
                <div className="emptystate">
                  <p className="es-t">Nothing repeats yet</p>
                  <p className="t-sec mut">
                    Rent and subscriptions are the same number every month and should never be
                    typed twelve times. Add them once and they post themselves.
                  </p>
                  {canWrite ? (
                    <p className="mt12">
                      <DChip kind="brand" onClick={() => setAdding(true)}>+ Add recurring</DChip>
                    </p>
                  ) : null}
                </div>
              }
            />
            <p className="t-sec mut mt16">
              Rent and subscriptions are the same number every month and should never be typed
              twelve times. Variable bills are a reminder, never an estimate posted as fact.
            </p>
              </>
            )}
          </>
        )}
      </div>

      <RecordModal
        open={recording}
        period={period}
        onClose={() => setRecording(false)}
        onSave={(input, opts) => {
          act.record.mutate(input, {
            onSuccess: () => toast.success(`${input.description} recorded`),
            onError: () => toast.error('It was not recorded. Nothing has been changed.'),
          });
          if (opts.monthly) {
            act.addRecurring.mutate({
              label: input.description,
              category: input.category,
              amountMinor: input.amountMinor,
              dayOfMonth: Number(input.spentOn.slice(8, 10)) || 1,
              ...(input.paidWith ? { paidWith: input.paidWith } : {}),
              ...(input.supplierName ? { supplierName: input.supplierName } : {}),
            });
          }
          if (!opts.another) setRecording(false);
        }}
      />

      <AddRecurringModal
        open={addingRecurring}
        onClose={() => setAdding(false)}
        onSave={(input) => {
          act.addRecurring.mutate(input, {
            onSuccess: () =>
              toast.success(
                input.amountMinor == null
                  ? `${input.label} will remind on the ${ordinal(input.dayOfMonth ?? 1)}`
                  : `${input.label} will post itself on the ${ordinal(input.dayOfMonth ?? 1)}`,
              ),
            onError: () => toast.error('It was not added.'),
          });
          setAdding(false);
        }}
      />

      <AttachModal
        line={attaching}
        onClose={() => setAttaching(null)}
        onSave={(doc) => {
          if (!attaching) return;
          act.attach.mutate(
            {
              id: attaching.id,
              documentName: doc.name,
              documentId: doc.id,
              description: attaching.description,
              amountMinor: attaching.amountMinor,
            },
            { onSuccess: () => toast.success(`${attaching.description} — invoice attached`) },
          );
          setAttaching(null);
        }}
      />
    </div>
  );
}

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};

function RecurringState({ line }: { line: RecurringLine }) {
  if (!line.isActive) return <Chip kind="outline" small>stopped</Chip>;
  if (line.state === 'posted') return <Chip kind="success" small>posted · invoice ✓</Chip>;
  if (line.state === 'posted_undocumented') {
    return <Chip kind="warn" small>posted · no document yet</Chip>;
  }
  if (line.state === 'not_yet') {
    return <span className="t-sec mut">posts on the {ordinal(line.dayOfMonth)}</span>;
  }
  return <Chip kind="outline" small>reminder on the {ordinal(line.dayOfMonth)}</Chip>;
}

/** A category opened: the lines inside it, and which have no paper. */
function CategoryPane({
  row,
  lines,
  canWrite,
  period,
  onAttach,
  highlightId,
  onClose,
}: {
  row: CategoryRow | undefined;
  lines: ExpenseLine[];
  canWrite: boolean;
  period: string;
  onAttach: (line: ExpenseLine) => void;
  /** The line a link pointed at — drawn with a brand border and scrolled into view. */
  highlightId?: string | null;
  onClose: () => void;
}) {
  const go = useNavigate();
  const { data: run } = usePayrollRun(period);
  if (!row) return null;
  const isPeople = row.category === 'people';
  return (
    <Pane
      onClose={onClose}
      foot={
        isPeople && run
          ? (
            <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => go("/accountant/people")}>
              Open the {monthName(period)} run
            </button>
          )
          : undefined
      }
      head={
        <PaneHead
          overline="Selected category"
          title={row.label}
          sub={`${row.count} line${row.count === 1 ? '' : 's'} · ${amount(row.totalMinor)}`}
          wrapTitle
        />
      }
    >
      {isPeople && run ? (
        <>
          <Card>
            <p className="t-ovl mut">The month&rsquo;s cost — this line</p>
            <p className="t-sec sec mt4">{amount(row.totalMinor)}</p>
            <p className="t-cap mut">what people cost the clinic in {monthName(period)}, on the P&amp;L</p>
          </Card>
          <Card>
            <p className="t-ovl mut">The {monthName(period)} run — a different scope</p>
            <p className="t-sec sec mt4">
              gross {amount(run.grossMinor)} · deductions −{amount(run.deductionsMinor)} · net to pay {amount(run.netMinor)}
            </p>
            <p className="t-cap mut">the rows of this settlement · cut-off {run.cutOff}</p>
          </Card>
          <Card>
            <p className="t-ovl mut">Two lanes</p>
            <p className="t-sec sec mt4">doctors on their deals · staff on salary</p>
          </Card>
          {run.netMinor !== row.totalMinor ? (
            <Card kind="warn">
              <p className="t-sec">
                The two numbers are not the same number: the expense line is the month&rsquo;s cost, the run is what
                this settlement moves. Their reconciliation is an open question for the owner.
              </p>
            </Card>
          ) : null}
        </>
      ) : null}

      {lines.map((l) => (
        <Card
          key={l.id}
          style={l.id === highlightId ? { borderColor: 'var(--brand-500)', borderWidth: 2 } : !l.documentId && !l.documentName ? { borderColor: 'var(--stroke-200)' } : undefined}
        >
          {l.id === highlightId ? (
            <span ref={(el) => el?.parentElement?.scrollIntoView({ block: 'nearest' })} />
          ) : null}
          <div className="flex" style={{ gap: 8, alignItems: 'flex-start' }}>
            <div className="grow" style={{ minWidth: 0 }}>
              <p className="t-strong">{l.description}</p>
              <p className="t-cap mut">
                {l.spentOn}
                {l.supplierName ? ` · ${l.supplierName}` : ''}
                {l.paidWith ? ` · ${l.paidWith}` : ''}
                {l.vatMinor ? ` · VAT ${amount(l.vatMinor)}` : ''}
              </p>
            </div>
            <p className="t-strong num">{amount(l.amountMinor)}</p>
          </div>
          <p className="mt8">
            {l.documentName || l.documentId ? (
              l.documentId ? (
                <DChip kind="success" onClick={() => void openExpenseDocument(l.documentId as string)}>
                  {l.documentName ?? 'invoice attached'} ↗
                </DChip>
              ) : (
                <Chip kind="success" small>{l.documentName}</Chip>
              )
            ) : l.sourceKind === 'stock' ? (
              <Chip kind="soft" small>from stock — the supplier invoice is on the delivery</Chip>
            ) : canWrite ? (
              <DChip kind="warn" onClick={() => onAttach(l)}>Attach the invoice</DChip>
            ) : (
              <Chip kind="warn" small>no document yet</Chip>
            )}
          </p>
        </Card>
      ))}
    </Pane>
  );
}

/** Record an expense — `g_m_rexp`, the two-column editor. */

/** A recurring line opened — `g_recur`. */
function RecurringPane({
  line,
  period,
  canWrite,
  onAttach,
  onClose,
}: {
  line: RecurringLine;
  period: string;
  canWrite: boolean;
  onAttach: () => void;
  onClose: () => void;
}) {
  const needsPaper = line.state === 'posted_undocumented';
  const history = useRecurringHistory(line.id).data;
  const monthShort = (p: string) => monthName(p).slice(0, 3);
  return (
    <Pane
      onClose={onClose}
      head={
        <PaneHead
          overline="Selected line"
          title={line.label}
          sub={line.amountMinor === null ? `reminds on the ${ordinal(line.dayOfMonth)}` : `posts on the ${ordinal(line.dayOfMonth)}`}
          wrapTitle
        />
      }
      foot={
        canWrite && needsPaper ? (
          <Button kind="outline" style={{ width: '100%' }} onClick={onAttach}>
            Attach the invoice
          </Button>
        ) : undefined
      }
    >
      <Card>
        <p className="t-ovl mut">{monthName(period).toUpperCase()} line</p>
        <p className="t-sec sec mt4">
          {line.amountMinor === null ? 'varies every month' : `EGP ${amount(line.amountMinor).replace(/^EGP\s*/, '')}`}
          {line.amountMinor !== null && line.state !== 'not_yet' && line.state !== 'reminder' ? ` · posted ${ordinal(line.dayOfMonth)}` : ''}
        </p>
        <p className="t-cap mut mt8">
          {needsPaper ? 'amber until the invoice is attached' : <RecurringState line={line} />}
        </p>
      </Card>
      {needsPaper ? (
        <Card kind="ghost" style={{ borderStyle: 'dashed', textAlign: 'center' }}>
          <p className="t-sec mut">attach the invoice or receipt</p>
        </Card>
      ) : null}
      <Card>
        <p className="t-ovl mut">Paid to</p>
        <p className="t-sec sec mt4">
          {[line.supplierName, line.paidWith].filter(Boolean).join(' · ') || 'not recorded'}
        </p>
      </Card>
      <Card>
        <p className="t-ovl mut">History</p>
        {history?.months.length ? (
          <>
            <p className="t-sec sec mt4">
              {history.months.slice(0, 6).map((m, i) => (
                <span key={m.expenseId}>
                  {i ? ' · ' : ''}
                  {monthShort(m.period)}{' '}
                  {m.documentId ? (
                    <button
                      type="button"
                      className="lnk brand"
                      style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer' }}
                      aria-label={`Open ${monthShort(m.period)} invoice`}
                      onClick={() => void openExpenseDocument(m.documentId as string)}
                    >
                      ✓
                    </button>
                  ) : (
                    '—'
                  )}
                </span>
              ))}
            </p>
            <p className="t-cap mut mt4">
              {history.months.length} month{history.months.length === 1 ? '' : 's'} posted ·{' '}
              {history.documents} document{history.documents === 1 ? '' : 's'}
            </p>
          </>
        ) : (
          <p className="t-sec mut mt4">Nothing posted yet.</p>
        )}
      </Card>
      {line.note ? (
        <Card>
          <p className="t-ovl mut">Note</p>
          <p className="t-sec sec mt4">{line.note}</p>
        </Card>
      ) : null}
    </Pane>
  );
}
